import { Add, Search } from '@mui/icons-material';
import {
  Box,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { IntegrationCard } from './IntegrationCard';
import { DeviceIntegrationModel, ServiceState } from '@ekkogmbh/apisdk';
import useDeviceIntegrations, {
  mapIntegrationTechnology,
} from 'src/DeviceIntegrationManagement/Providers/DeviceIntegrationProvider';

type StatusFilter = 'ALL' | ServiceState.ONLINE | ServiceState.OFFLINE;

const CARD_MIN_WIDTH = 300;

const matchesStatus = (integration: DeviceIntegrationModel, filter: StatusFilter): boolean => {
  if (filter === 'ALL') {
    return true;
  }
  return (integration.serviceData?.state ?? ServiceState.UNKNOWN) === filter;
};

const matchesSearch = (integration: DeviceIntegrationModel, query: string): boolean => {
  if (query === '') {
    return true;
  }
  const needle = query.toLowerCase();

  const { integrationName: name, coordinate, nodePath, capabilities } = integration;
  const technology = mapIntegrationTechnology(name);

  const haystack = [name, coordinate, nodePath, technology.name, technology.manufacturer?.name, ...(capabilities ?? [])]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(needle);
};

interface Props {
  onAdd: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onClick: () => void;
}

/** The configured-integrations gallery: search + status filter + card grid. */
export const IntegrationGallery = ({ onAdd, onDelete, onClick, onEdit }: Props): React.JSX.Element => {
  const { integrations } = useDeviceIntegrations();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('ALL');

  const filteredIndices = useMemo(
    () =>
      integrations.reduce((carry: number[], entry, index) => {
        if (matchesStatus(entry, status) && matchesSearch(entry, search)) {
          carry.push(index);
        }
        return carry;
      }, []),
    [integrations, status, search],
  );

  return (
    <Stack spacing={2.5}>
      {/* Controls */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent={'space-between'}
      >
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size={'small'}
          placeholder={'Search by system, manufacturer or coordinate'}
          sx={{ minWidth: { sm: 320 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position={'start'}>
                <Search fontSize={'small'} color={'action'} />
              </InputAdornment>
            ),
          }}
        />
        <ToggleButtonGroup
          size={'small'}
          exclusive
          value={status}
          onChange={(_, value: StatusFilter | null) => value !== null && setStatus(value)}
        >
          <ToggleButton value={'ALL'} sx={{ textTransform: 'none', px: 2 }}>
            All
          </ToggleButton>
          <ToggleButton value={ServiceState.ONLINE} sx={{ textTransform: 'none', px: 2 }}>
            Online
          </ToggleButton>
          <ToggleButton value={ServiceState.OFFLINE} sx={{ textTransform: 'none', px: 2 }}>
            Offline
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_MIN_WIDTH}px, 1fr))`,
          gap: 2,
          alignItems: 'stretch',
        }}
      >
        {filteredIndices.map((integrationIndex, i) => (
          <IntegrationCard
            key={i}
            integrationIndex={integrationIndex}
            onDelete={onDelete}
            onClick={onClick}
            onEdit={onEdit}
          />
        ))}

        {/* Add card */}
        <Paper
          variant={'outlined'}
          onClick={onAdd}
          sx={{
            p: 2,
            borderRadius: 2,
            minHeight: 180,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            cursor: 'pointer',
            color: 'text.secondary',
            borderStyle: 'dashed',
            transition: 'border-color 120ms, color 120ms, background-color 120ms',
            '&:hover': { borderColor: 'primary.main', color: 'primary.main', backgroundColor: 'action.hover' },
          }}
        >
          <Add />
          <Typography variant={'body2'} fontWeight={600}>
            Add integration
          </Typography>
        </Paper>
      </Box>

      {filteredIndices.length === 0 && (
        <Typography variant={'body2'} color={'text.secondary'} sx={{ textAlign: 'center', py: 2 }}>
          No integrations match the current filter.
        </Typography>
      )}
    </Stack>
  );
};
